import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  AiSplitItemDto,
  AiSplitProposalDto,
  confirmAiSplits,
  generateAiSplitProposal,
} from "../../../api/aiRepairSplitService";
import {
  getIncidentTypesForAsset,
  normalizeIncidentTypeForAsset,
} from "../../../data/Fleet/IncidentType";
import useAuth from "../../../hooks/useAuth";
import {
  calculateAiSplitAllocatedTotal,
  roundCurrency,
} from "../../../helpers/aiRepairSplitCalculations";
import {
  getInvoicePreview,
  InvoicePreview,
} from "../../../api/invoicePreviewService";

interface Props {
  open: boolean;
  incident: any;
  repair: any;
  assetType: string;
  onClose: () => void;
}

const getErrorMessage = (error: any) => {
  const message = error?.response?.data;
  if (typeof message === "string") return message;
  if (typeof message?.message === "string") return message.message;
  return "The invoice could not be analyzed. Make sure a readable invoice is attached and try again.";
};

interface EditableAiSplit extends Omit<
  AiSplitItemDto,
  "complaint" | "finalCost" | "incidentType"
> {
  complaint: string;
  finalCost: number;
  incidentType: string;
}

const AiRepairSplitsDialog = ({
  open,
  incident,
  repair,
  assetType,
  onClose,
}: Props) => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [proposal, setProposal] = useState<AiSplitProposalDto | null>(null);
  const [splits, setSplits] = useState<EditableAiSplit[]>([]);
  const [parentIncidentType, setParentIncidentType] = useState("");
  const [parentAmount, setParentAmount] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [invoicePreview, setInvoicePreview] = useState<InvoicePreview | null>(
    null,
  );
  const [invoicePreviewError, setInvoicePreviewError] = useState("");
  const [isInvoiceLoading, setIsInvoiceLoading] = useState(false);

  const incidentDate = String(incident?.openDate ?? "").split("T")[0];
  const targetAmount = Number(proposal?.repairAmount ?? repair?.finalCost ?? 0);
  const incidentTypeOptions = getIncidentTypesForAsset(assetType).filter(
    ({ value }) => value,
  );
  const childSplitsTotal = useMemo(
    () =>
      roundCurrency(
        splits.reduce(
          (total, split) => total + Number(split.finalCost || 0),
          0,
        ),
      ),
    [splits],
  );
  const allocatedTotal = calculateAiSplitAllocatedTotal(
    parentAmount,
    splits.map((split) => split.finalCost),
  );
  const totalsMatch = roundCurrency(targetAmount) === allocatedTotal;
  const hasInvalidParentType = Boolean(
    parentIncidentType &&
    !incidentTypeOptions.some(({ value }) => value === parentIncidentType),
  );
  const hasInvalidFields = splits.some((split) => {
    const amount = Number(split.finalCost);
    return (
      !incidentTypeOptions.some(({ value }) => value === split.incidentType) ||
      !split.complaint.trim() ||
      !Number.isFinite(amount) ||
      amount <= 0 ||
      roundCurrency(amount) !== amount
    );
  });

  const generateMutation = useMutation({
    mutationFn: () =>
      generateAiSplitProposal(String(incident.id), String(repair.id), token),
    onSuccess: (nextProposal) => {
      setProposal(nextProposal);
      setParentIncidentType(
        normalizeIncidentTypeForAsset(
          nextProposal.parent?.incidentType,
          assetType,
        ),
      );
      setParentAmount(
        Number(
          nextProposal.parentAmount ?? nextProposal.parent?.finalCost ?? 0,
        ),
      );
      setSplits(
        nextProposal.splits.map((split) => ({
          ...split,
          complaint: split.complaint ?? "",
          finalCost: Number(split.finalCost ?? 0),
          incidentType: normalizeIncidentTypeForAsset(
            split.incidentType,
            assetType,
          ),
          completedDate: incidentDate,
          reviewIssues: split.reviewIssues ?? [],
        })),
      );
      setErrorMessage("");
    },
    onError: (error) => setErrorMessage(getErrorMessage(error)),
  });

  const confirmMutation = useMutation({
    mutationFn: () =>
      confirmAiSplits(
        String(incident.id),
        {
          repairId: proposal!.repairId,
          expectedRepairAmount: proposal!.repairAmount,
          parentAmount: proposal!.parent ? parentAmount : null,
          parentIncidentType: parentIncidentType || null,
          splits: splits.map((split) => ({
            complaint: split.complaint.trim(),
            finalCost: Number(split.finalCost),
            incidentType: split.incidentType,
            completedDate: incidentDate,
          })),
        },
        token,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["incidents"] });
      queryClient.invalidateQueries({ queryKey: ["repairs", incident.id] });
      onClose();
    },
    onError: (error) => setErrorMessage(getErrorMessage(error)),
  });

  useEffect(() => {
    if (open && repair?.id) generateMutation.mutate();
    if (!open) {
      setProposal(null);
      setSplits([]);
      setParentIncidentType("");
      setParentAmount(0);
      setErrorMessage("");
    }
    // Generate only when the dialog is opened for a repair.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, repair?.id]);

  useEffect(() => {
    if (!open || !repair?.id) return;

    let cancelled = false;
    let previewUrl = "";
    setIsInvoiceLoading(true);
    setInvoicePreview(null);
    setInvoicePreviewError("");

    getInvoicePreview(String(repair.id), token)
      .then((preview) => {
        previewUrl = preview.url;
        if (cancelled) {
          window.URL.revokeObjectURL(preview.url);
          return;
        }
        setInvoicePreview(preview);
      })
      .catch(() => {
        if (!cancelled) {
          setInvoicePreviewError(
            "The attached invoice could not be previewed.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setIsInvoiceLoading(false);
      });

    return () => {
      cancelled = true;
      if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    };
  }, [open, repair?.id, token]);

  const updateSplit = (index: number, updates: Partial<EditableAiSplit>) => {
    setSplits((current) =>
      current.map((split, splitIndex) =>
        splitIndex === index ? { ...split, ...updates } : split,
      ),
    );
  };

  const warnings = proposal?.reviewIssues ?? [];
  const invoiceDiffers =
    proposal?.invoiceTotal != null &&
    roundCurrency(Number(proposal.invoiceTotal)) !==
      roundCurrency(targetAmount);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{ sx: { height: { md: "90vh" } } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {proposal && !proposal.requiresSplit ? (
          <CheckCircleOutlineIcon color="success" />
        ) : (
          <AutoAwesomeIcon color="primary" />
        )}
        {proposal && !proposal.requiresSplit
          ? "Invoice review"
          : "Review AI split proposal"}
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
        }}
      >
        <Box
          sx={{
            flex: "1 1 52%",
            minWidth: 0,
            overflowY: "auto",
            pr: { md: 1 },
          }}
        >
          {generateMutation.isPending ? (
            <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
              <CircularProgress />
              <Typography>
                Analyzing repair details and attached invoice…
              </Typography>
            </Stack>
          ) : errorMessage && !proposal ? (
            <Alert severity="error">{errorMessage}</Alert>
          ) : proposal && !proposal.requiresSplit ? (
            <Stack
              alignItems="center"
              spacing={2}
              sx={{ py: 4, textAlign: "center" }}
            >
              <CheckCircleOutlineIcon color="success" sx={{ fontSize: 56 }} />
              <Typography variant="h6">No split needed</Typography>
              <Typography color="text.secondary">
                {proposal.message || "This invoice does not require a split."}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                <Typography>
                  Repair amount:{" "}
                  <strong>${proposal.repairAmount.toFixed(2)}</strong>
                </Typography>
                {proposal.invoiceTotal != null && (
                  <Typography>
                    Invoice total:{" "}
                    <strong>${Number(proposal.invoiceTotal).toFixed(2)}</strong>
                  </Typography>
                )}
              </Box>
              <Alert severity="success" sx={{ width: "100%" }}>
                No incidents were changed and there is nothing to confirm.
              </Alert>
              {warnings.map((warning, index) => (
                <Alert
                  severity="warning"
                  key={`${warning}-${index}`}
                  sx={{ width: "100%" }}
                >
                  {warning}
                </Alert>
              ))}
            </Stack>
          ) : proposal ? (
            <Stack spacing={2}>
              <Alert severity="info">
                AI suggestions are not saved yet. Review every field, edit as
                needed, then confirm.
              </Alert>
              {proposal.message && (
                <Alert severity="info">{proposal.message}</Alert>
              )}
              {warnings.map((warning, index) => (
                <Alert severity="warning" key={`${warning}-${index}`}>
                  {warning}
                </Alert>
              ))}
              {proposal.requiresSplit && invoiceDiffers && (
                <Alert severity="warning">
                  Invoice total (${Number(proposal.invoiceTotal).toFixed(2)})
                  does not match the repair amount (${targetAmount.toFixed(2)}).
                  Review before confirming.
                </Alert>
              )}
              {proposal.requiresSplit && proposal.parent && (
                <Box
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 2,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ mb: 1 }}
                  >
                    Original incident
                  </Typography>
                  {proposal.parent.reviewIssues.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      {proposal.parent.reviewIssues.join(" ")}
                    </Alert>
                  )}
                  <FormControl fullWidth>
                    <InputLabel>Parent Incident Type</InputLabel>
                    <Select
                      value={parentIncidentType}
                      label="Parent Incident Type"
                      onChange={(event) =>
                        setParentIncidentType(event.target.value)
                      }
                    >
                      <MenuItem value="">
                        <em>Keep current incident type</em>
                      </MenuItem>
                      {parentIncidentType &&
                        !incidentTypeOptions.some(
                          ({ value }) => value === parentIncidentType,
                        ) && (
                          <MenuItem value={parentIncidentType} disabled>
                            Unknown type: {parentIncidentType} (review required)
                          </MenuItem>
                        )}
                      {incidentTypeOptions.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    label="Parent Amount"
                    value={`$${parentAmount.toFixed(2)}`}
                    disabled
                    helperText="Amount retained on the original incident"
                    sx={{ mt: 2 }}
                  />
                </Box>
              )}
              {proposal.requiresSplit &&
                splits.map((split, index) => (
                  <Box
                    key={index}
                    sx={{
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{ mb: 1 }}
                    >
                      Split {index + 1}
                    </Typography>
                    {split.reviewIssues.length > 0 && (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        {split.reviewIssues.join(" ")}
                      </Alert>
                    )}
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        required
                        fullWidth
                        label="Complaint"
                        value={split.complaint}
                        onChange={(event) =>
                          updateSplit(index, { complaint: event.target.value })
                        }
                      />
                      <FormControl required fullWidth>
                        <InputLabel>Incident Type</InputLabel>
                        <Select
                          value={split.incidentType}
                          label="Incident Type"
                          onChange={(event) =>
                            updateSplit(index, {
                              incidentType: event.target.value,
                            })
                          }
                        >
                          {!incidentTypeOptions.some(
                            ({ value }) => value === split.incidentType,
                          ) &&
                            split.incidentType && (
                              <MenuItem value={split.incidentType} disabled>
                                Unknown type: {split.incidentType} (review
                                required)
                              </MenuItem>
                            )}
                          {incidentTypeOptions.map(({ value, label }) => (
                            <MenuItem key={value} value={value}>
                              {label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                      sx={{ mt: 2 }}
                    >
                      <TextField
                        required
                        fullWidth
                        type="number"
                        label="Amount"
                        inputProps={{ min: 0.01, step: 0.01 }}
                        value={split.finalCost}
                        onChange={(event) =>
                          updateSplit(index, {
                            finalCost: Number(event.target.value),
                          })
                        }
                      />
                      <TextField
                        fullWidth
                        label="Completed Date"
                        type="date"
                        value={incidentDate}
                        InputLabelProps={{ shrink: true }}
                        disabled
                        helperText="Uses the incident date"
                      />
                    </Stack>
                  </Box>
                ))}
              {proposal.requiresSplit && (
                <Alert severity={totalsMatch ? "success" : "error"}>
                  Allocated total: ${allocatedTotal.toFixed(2)} · Repair amount:
                  ${targetAmount.toFixed(2)} (parent ${parentAmount.toFixed(2)}{" "}
                  + child splits ${childSplitsTotal.toFixed(2)})
                  {!totalsMatch && " — amounts must match before confirmation."}
                </Alert>
              )}
              {proposal.requiresSplit && !proposal.canConfirm && (
                <Alert severity="warning">
                  AI marked the original proposal as incomplete. Review the
                  issues and correct the required fields below before
                  confirmation.
                </Alert>
              )}
              {proposal.requiresSplit && hasInvalidFields && (
                <Alert severity="warning">
                  Complete all required fields and use positive amounts with no
                  more than two decimal places.
                </Alert>
              )}
              {proposal.requiresSplit && hasInvalidParentType && (
                <Alert severity="warning">
                  Select a valid parent incident type or keep the current type.
                </Alert>
              )}
              {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
            </Stack>
          ) : null}
        </Box>
        <Box
          sx={{
            flex: "1 1 48%",
            minWidth: 0,
            minHeight: { xs: 420, md: 0 },
            display: "flex",
            flexDirection: "column",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              Invoice preview
            </Typography>
            {invoicePreview && (
              <Button
                size="small"
                component="a"
                href={invoicePreview.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in new tab
              </Button>
            )}
          </Box>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "grey.100",
              p: invoicePreview ? 0 : 3,
            }}
          >
            {isInvoiceLoading ? (
              <Stack alignItems="center" spacing={2}>
                <CircularProgress />
                <Typography color="text.secondary">Loading invoice…</Typography>
              </Stack>
            ) : invoicePreviewError ? (
              <Alert severity="warning">{invoicePreviewError}</Alert>
            ) : invoicePreview?.contentType.includes("pdf") ? (
              <Box
                component="iframe"
                src={invoicePreview.url}
                title="Invoice preview"
                sx={{
                  width: "100%",
                  height: "100%",
                  minHeight: 500,
                  border: 0,
                  bgcolor: "white",
                }}
              />
            ) : invoicePreview ? (
              <Box
                component="img"
                src={invoicePreview.url}
                alt="Invoice preview"
                sx={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            ) : null}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {proposal && !proposal.requiresSplit ? "Close" : "Cancel"}
        </Button>
        {(!proposal || proposal.requiresSplit) && (
          <Button
            onClick={() => generateMutation.mutate()}
            disabled={generateMutation.isPending || confirmMutation.isPending}
            startIcon={<AutoAwesomeIcon />}
          >
            Regenerate
          </Button>
        )}
        {proposal?.requiresSplit && (
          <Button
            variant="contained"
            onClick={() => confirmMutation.mutate()}
            disabled={
              !splits.length ||
              !totalsMatch ||
              hasInvalidFields ||
              hasInvalidParentType ||
              generateMutation.isPending ||
              confirmMutation.isPending
            }
          >
            {confirmMutation.isPending ? "Saving…" : "Confirm & Save Splits"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AiRepairSplitsDialog;
